import { useCaseLabels } from '@/i18n/enums/case';
// i18n: translated
/**
 * 通知ドロワー (notification-drawer-redesign、2026-09-06 高橋の案)
 *
 * 右側からスライドインする通知パネル。通知一覧・既読マーク・リソース遷移を提供する。
 * SPEC 7.1.3 通知ドロワー準拠。
 *
 * ## 構成
 * - ヘッダー: 「通知」+ 未読数 / 「すべて既読」/ ケバブ (対応履歴を CSV 出力) / 閉じる
 * - タブ「すべて / 未読」(既定 未読。未読は API 側で絞る = 従来どおり) + 種類の絞り込み (読み込み済みの一覧に対する FE 側の絞り込み)
 * - 日付見出し (JST) の下に 2 行の行: 1 行目「依頼者名 + 主文 + 時刻」、2 行目「種類 · 事件種別 / 面」。
 *   補足 (差し戻し理由など) があるときだけ 3 行目
 *
 * ## 変えていないもの
 * - 遷移先は `notificationDestination` (drawer と popup で共有)。title 文字列は解析しない
 * - 既読化 / 一括既読 / ページング / open 時の refetch / ESC・オーバーレイで閉じる
 */
import { useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocale, useT } from '@/i18n';
import { commonNs } from '@/i18n/namespaces/common';
import { shellNs } from '@/i18n/namespaces/shell';
import {
  useNotifications,
  useMarkAsRead,
  useMarkAllAsRead,
  useExportHistory,
  selectUnreadNotifications,
  reconcileOptimisticReadIds,
  NOTIFICATION_PAGE_SIZE,
  type OptimisticUnreadAction,
} from '@/hooks/useNotifications';
import type { Notification } from '@/types';
// PR-19: raw LINE userId (U + 32 hex) を末尾 4 桁 fallback に置換し、UI に全量を晒さない
// (既存 DB データの notification.title / clientName 内にも raw が残るため描画層で正規化)。
import { sanitizeLineUserIdInText } from '@/utils/lineDisplayName';
// PR-21: drawer は NotificationBell から常時マウントされ CSS で見せ隠ししているため、
// `useNotifications()` は mount 時に 1 回 fetch するきり再取得しない。open 遷移時のみ refetch する。
import { useRefetchOnOpen } from '@/hooks/useRefetchOnOpen';
// 通知イベント → 遷移先の解決。drawer と新着ポップアップで同一 resolver を共有し、
// 遷移先の定義をここに二重実装しない (notification-event-deep-link-routing)。
import { notificationDestination } from '@/utils/notificationDestination';
import {
  NOTIFICATION_CATEGORY_FILTER_OPTIONS,
  notificationBreadcrumb,
  notificationCategory,
  notificationCategoryName,
  type NotificationCategory,
} from '@/utils/notificationCategory';
import {
  formatNotificationClockTime,
  formatNotificationPrimaryLine,
  groupNotificationsByDate,
  notificationDisplayAt,
  notificationSupplementLines,
} from '@/utils/notificationDrawerFormat';

interface NotificationDrawerProps {
  /** ドロワー表示状態 */
  isOpen: boolean;
  /** 閉じるコールバック */
  onClose: () => void;
  /** FL: 既読操作後にバッジ未読数を楽観更新する (NotificationBell の useUnreadCount 由来) */
  onUnreadChange?: (action: OptimisticUnreadAction) => void;
  /** S3: 一括既読の完了後にバッジ未読数をサーバから引き直す */
  onUnreadRefetch?: () => void;
  /** ヘッダーの未読数 pill (ベルのバッジと同じ値)。未指定なら出さない */
  unreadCount?: number;
}

type DrawerTab = 'unread' | 'all';

/** 種類の絞り込み。'' = すべての種類 */
type CategoryFilter = '' | NotificationCategory;

const HEADER_HEIGHT_CLASS = 'top-14';

export default function NotificationDrawer({
  isOpen,
  onClose,
  onUnreadChange,
  onUnreadRefetch,
  unreadCount,
}: NotificationDrawerProps) {
  // ドロワーの枠の文言 (admin-english-toggle-20260924)。通知の本文・種類名・日付見出しは phase 2
  const t = useT(shellNs);
  const tc = useT(commonNs);
  const { locale } = useLocale();
  const [tab, setTab] = useState<DrawerTab>('unread');
  const [category, setCategory] = useState<CategoryFilter>('');
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // S2: 未読タブは API 側 (unreadOnly) で絞る。窓から外れた未読にもページングで到達できる (従来どおり)。
  // 「すべて」タブは既読込みの一覧を同じページングで引く。
  const { notifications, loading, refetch, hasMore, loadingMore, loadMore } = useNotifications({
    enabled: isOpen,
    unreadOnly: tab === 'unread',
    limit: NOTIFICATION_PAGE_SIZE,
  });
  const { markAllAsRead, loading: markAllLoading } = useMarkAllAsRead();
  // S3: 一括既読の結果 (件数表示用)。null は「未実行 or 失敗」。
  const [markAllResult, setMarkAllResult] = useState<{
    processedCount: number;
    failed: boolean;
  } | null>(null);
  const { markAsRead } = useMarkAsRead();
  // steering 321: 対応履歴 (通知履歴) CSV export。空履歴でも header 行のみの CSV が
  // download されるため、常に実行可 (download 中のみ disabled)。ケバブメニューから呼ぶ。
  const { exportHistory, loading: exportLoading } = useExportHistory();

  // FL: optimistic に既読化した notifId。未読タブでは即時に隠し、すべてタブでは既読の見た目にする。
  const [optimisticReadIds, setOptimisticReadIds] = useState<string[]>([]);

  // サーバ一覧が更新されたら optimistic 集合を整理する (反映済み id を落とす)。
  useEffect(() => {
    setOptimisticReadIds((prev) => reconcileOptimisticReadIds(prev, notifications));
  }, [notifications]);

  // PR-21 / F-17: drawer を開いた瞬間に最新通知を再取得する (open エッジでのみ発火)。
  useRefetchOnOpen(isOpen, refetch);

  /** ESC キーで閉じる */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  /** ケバブメニューの外側クリックで閉じる */
  useEffect(() => {
    if (!menuOpen) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [menuOpen]);

  /** 一括既読 */
  const handleMarkAllRead = useCallback(async () => {
    // S3: 楽観更新はしない。一括既読は複数リクエストに分割され、途中で止まる
    // (上限到達・chunk 失敗) ことが正規の結果としてあり得るため。終わったら実状態を引き直す。
    const res = await markAllAsRead();
    setMarkAllResult(res ? { processedCount: res.processedCount, failed: false } : null);
    onUnreadChange?.('clear');
    onUnreadRefetch?.();
    refetch();
  }, [markAllAsRead, onUnreadChange, onUnreadRefetch, refetch]);

  /** 個別既読 (optimistic: 即時に反映し、バッジを 1 減らす) */
  const handleMarkRead = useCallback(
    async (notifId: string) => {
      setOptimisticReadIds((prev) => (prev.includes(notifId) ? prev : [...prev, notifId]));
      onUnreadChange?.('decrement');
      const res = await markAsRead(notifId);
      if (res === null) {
        setOptimisticReadIds((prev) => prev.filter((id) => id !== notifId));
        onUnreadChange?.('clear');
        return;
      }
      refetch();
    },
    [markAsRead, onUnreadChange, refetch],
  );

  /**
   * 表示する一覧。
   * - 未読タブ: 未読だけ (optimistic 既読は隠す)
   * - すべてタブ: 全件 (optimistic 既読は既読の見た目にする)
   * - 種類の絞り込みは読み込み済みの一覧に対して行う (API に種類の filter は無い)
   */
  const visibleNotifications = useMemo(() => {
    const base =
      tab === 'unread'
        ? selectUnreadNotifications(notifications).filter(
            (n) => !optimisticReadIds.includes(n.notifId),
          )
        : notifications.map((n) =>
            !n.isRead && optimisticReadIds.includes(n.notifId) ? { ...n, isRead: true } : n,
          );
    return category ? base.filter((n) => notificationCategory(n.type) === category) : base;
  }, [notifications, optimisticReadIds, tab, category]);

  const sections = useMemo(
    () => groupNotificationsByDate(visibleNotifications, locale),
    [visibleNotifications, locale],
  );

  const emptyLabel =
    category !== ''
      ? t('notifications.emptyForType')
      : tab === 'unread'
        ? t('notifications.emptyUnread')
        : t('notifications.empty');

  return (
    <>
      {/* オーバーレイ */}
      <div
        className={`fixed inset-0 z-40 bg-black/40 transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
        style={{ top: '3.5rem' }}
        onClick={onClose}
      />

      {/* ドロワー本体 (スライドイン)。幅は 384px → 480px (書類名の折り返しを減らす) */}
      <div
        className={`fixed right-0 z-50 flex w-[30rem] max-w-full flex-col border-l border-gray-200 bg-white shadow-xl transition-transform duration-300 ease-in-out dark:border-gray-800 dark:bg-gray-900 ${isOpen ? 'translate-x-0' : 'translate-x-full'} ${HEADER_HEIGHT_CLASS}`}
        style={{ bottom: 0 }}
        aria-hidden={!isOpen}
      >
        {/* ヘッダー */}
        <div className="flex h-14 shrink-0 items-center justify-between border-b border-gray-200 px-4 dark:border-gray-800">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('notifications.title')}
            </h2>
            {typeof unreadCount === 'number' && unreadCount > 0 && (
              <span
                className="rounded-full bg-gray-200 px-2 py-0.5 text-xs font-semibold text-gray-700 dark:bg-gray-700 dark:text-gray-200"
                data-testid="notification-unread-pill"
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {/* S3: 一括既読は複数リクエストに分割されるため、完了までボタンを無効化して二重発火を防ぐ */}
            <button
              type="button"
              onClick={handleMarkAllRead}
              disabled={markAllLoading}
              className="rounded px-2 py-1 text-xs text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-40 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white"
            >
              {markAllLoading ? t('notifications.markingAllRead') : t('notifications.markAllRead')}
            </button>
            {/* ケバブ: 使用頻度の低い操作 (CSV 出力) をまとめる */}
            <div ref={menuRef} className="relative">
              <button
                type="button"
                onClick={() => setMenuOpen((prev) => !prev)}
                aria-label={t('notifications.moreActions')}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                className="rounded-lg p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white"
              >
                {/* Heroicons: ellipsis-vertical */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                  className="h-5 w-5"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 6.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 12.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 18.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z"
                  />
                </svg>
              </button>
              {menuOpen && (
                <div
                  role="menu"
                  className="absolute right-0 top-full z-10 mt-1 w-52 rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800"
                >
                  {/* steering 321: 対応履歴 (通知履歴) CSV 出力。既存 BE endpoint
                      GET /admin/notifications/history/export を叩き Blob download する。 */}
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      void exportHistory();
                    }}
                    disabled={exportLoading}
                    className="block w-full px-3 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 dark:text-gray-200 dark:hover:bg-gray-700"
                  >
                    {exportLoading
                      ? t('notifications.exporting')
                      : t('notifications.exportHistory')}
                  </button>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white"
              aria-label={tc('actions.close')}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="h-5 w-5"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* タブ + 種類の絞り込み */}
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-200 px-4 dark:border-gray-800">
          <div role="tablist" aria-label={t('notifications.rangeTabs')} className="flex gap-4">
            {(
              [
                { key: 'all', label: t('notifications.tabAll') },
                { key: 'unread', label: t('notifications.tabUnread') },
              ] as { key: DrawerTab; label: string }[]
            ).map((tabDef) => (
              <button
                key={tabDef.key}
                type="button"
                role="tab"
                aria-selected={tab === tabDef.key}
                onClick={() => setTab(tabDef.key)}
                className={`-mb-px border-b-2 px-1 py-2.5 text-sm transition-colors ${
                  tab === tabDef.key
                    ? 'border-brand-500 font-semibold text-brand-600 dark:text-brand-400'
                    : 'border-transparent text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
                }`}
              >
                {tabDef.label}
              </button>
            ))}
          </div>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as CategoryFilter)}
            aria-label={t('notifications.filterByType')}
            className="max-w-[11rem] rounded-lg border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
          >
            <option value="">{t('notifications.allTypes')}</option>
            {NOTIFICATION_CATEGORY_FILTER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {notificationCategoryName(o.value, locale)}
              </option>
            ))}
          </select>
        </div>

        {/* S3: 一括既読の結果。分割処理なので「何件処理したか」を実数で出す */}
        {markAllResult && (
          <div className="shrink-0 border-b border-gray-200 px-4 py-2 text-xs text-gray-500 dark:border-gray-800 dark:text-gray-400">
            {t('notifications.markedRead', { count: markAllResult.processedCount })}
          </div>
        )}

        {/* 通知リスト */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
            </div>
          ) : visibleNotifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              {/* Heroicons: bell-slash */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="mb-3 h-10 w-10 text-gray-400 dark:text-gray-600"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9.143 17.082a24.248 24.248 0 0 0 3.844.148m-3.844-.148a23.856 23.856 0 0 1-5.455-1.31 8.964 8.964 0 0 0 2.3-5.542m3.155 6.852a3 3 0 0 0 5.667 1.97m1.965-2.277L21 21m-4.225-4.225a23.9 23.9 0 0 0 3.536-1.003A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6.53 6.53m10.245 10.245L6.53 6.53M3 3l3.53 3.53"
                />
              </svg>
              <p className="text-sm text-gray-500">{emptyLabel}</p>
            </div>
          ) : (
            sections.map((section) => (
              <section key={section.key || 'unknown'} aria-label={section.heading}>
                <h3
                  className="sticky top-0 z-[1] border-b border-gray-200 bg-gray-50 px-4 py-1.5 text-xs font-semibold text-gray-600 dark:border-gray-800 dark:bg-gray-900/95 dark:text-gray-300"
                  data-testid="notification-date-heading"
                >
                  {section.heading}
                </h3>
                <ul className="divide-y divide-gray-200 dark:divide-gray-800">
                  {section.items.map((notif) => (
                    <NotificationRow
                      key={notif.notifId}
                      notification={notif}
                      onMarkRead={handleMarkRead}
                      onNavigated={onClose}
                    />
                  ))}
                </ul>
              </section>
            ))
          )}

          {/* S2: 1 ページに収まらない場合の追加読み込み。押されたときだけ次ページを取りに行く。
              種類で絞り込んでいる間も出す (続きを読み込むと絞り込みに追加される)。 */}
          {!loading && hasMore && (
            <div className="flex justify-center border-t border-gray-200 p-3 dark:border-gray-800">
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                className="rounded px-3 py-1.5 text-sm text-brand-600 transition-colors hover:bg-brand-50 disabled:cursor-not-allowed disabled:opacity-50 dark:text-brand-400 dark:hover:bg-gray-800"
              >
                {loadingMore ? tc('status.loadingEllipsis') : tc('actions.loadMore')}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ==============================
// 通知の行 (2 行構成)
// ==============================

interface NotificationRowProps {
  notification: Notification;
  onMarkRead: (notifId: string) => void;
  /** 遷移が成立したときにドロワーを閉じる (遷移先が overlay に覆われるのを防ぐ) */
  onNavigated: () => void;
}

/** 2 行目の種類アイコン (Heroicons 24 outline)。 */
function CategoryIcon({ category }: { category: NotificationCategory }) {
  const path = CATEGORY_ICON_PATHS[category] ?? CATEGORY_ICON_PATHS.other;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className="h-3.5 w-3.5 shrink-0"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d={path} />
    </svg>
  );
}

const CATEGORY_ICON_PATHS: Record<NotificationCategory, string> = {
  // document-text
  document:
    'M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z',
  // check-circle
  review: 'M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z',
  // chat-bubble-left-ellipsis
  message:
    'M8.625 9.75a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 0 1 .778-.332 48.294 48.294 0 0 0 5.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z',
  // bell
  guide:
    'M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0',
  // envelope
  email:
    'M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75',
  // briefcase
  case: 'M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 0 0 .75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 0 0-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0 1 12 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 0 1-.673-.38m0 0A2.18 2.18 0 0 1 3 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 0 1 3.413-.387m7.5 0V5.25A2.25 2.25 0 0 0 13.5 3h-3a2.25 2.25 0 0 0-2.25 2.25v.894m7.5 0a48.667 48.667 0 0 0-7.5 0',
  // clipboard-document-check
  task: 'M11.35 3.836c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m8.9-4.414c.376.023.75.05 1.124.08 1.131.094 1.976 1.057 1.976 2.192V16.5A2.25 2.25 0 0 1 18 18.75h-2.25m-7.5-10.5H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V18.75m-7.5-10.5h6.375c.621 0 1.125.504 1.125 1.125v9.375m-8.25-3 1.5 1.5 3-3.75',
  // information-circle
  other:
    'm11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z',
};

function NotificationRow({ notification, onMarkRead, onNavigated }: NotificationRowProps) {
  const navigate = useNavigate();
  // 行の枠 (件数付きの文・種類・面・事件種別) だけ表示言語に合わせる。依頼者名・書類名・本文はデータ
  const { locale } = useLocale();
  const caseLabels = useCaseLabels();

  const category = notificationCategory(notification.type);
  // 主文: 書類系は書類名 (集約なら「N件」付き)、メッセージは本文冒頭、他は従来の文 (title 文字列は解析しない)
  // LINE の userId を置き換える「LINE ユーザー」の表記も表示言語で出す (admin-english-final-e2e-20260925)
  const primary = sanitizeLineUserIdInText(
    formatNotificationPrimaryLine(notification, locale),
    locale,
  );
  const supplements = notificationSupplementLines(notification, locale).map((l) =>
    sanitizeLineUserIdInText(l, locale),
  );
  const breadcrumb = notificationBreadcrumb(notification, locale, caseLabels.caseType);
  const timeLabel = formatNotificationClockTime(notificationDisplayAt(notification));
  // PR-19: clientName にも LINE userId が混入し得るため描画層で正規化する
  const clientName = sanitizeLineUserIdInText(notification.clientName, locale);

  /**
   * 通知クリック: 既読化と遷移を独立に行う。
   * - 遷移先は `notificationDestination` が **notification.type** から決める
   * - 既読化は fire-and-forget。遷移を既読化の成否に依存させない
   * - 解決できない通知 (未知 type / 必要な ID 欠落) は遷移しない (無関係な画面へ飛ばさない)
   * - 遷移が成立したらドロワーを閉じる
   */
  const handleClick = useCallback(() => {
    if (!notification.isRead) {
      onMarkRead(notification.notifId);
    }
    const destination = notificationDestination(notification);
    if (!destination) return;
    navigate(destination);
    onNavigated();
  }, [notification, onMarkRead, navigate, onNavigated]);

  const isRead = notification.isRead;

  return (
    <li
      onClick={handleClick}
      data-testid="notification-row"
      data-read={isRead ? 'true' : 'false'}
      className={`cursor-pointer px-4 py-3 transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/60 ${
        isRead ? '' : 'bg-indigo-50/50 dark:bg-gray-900/50'
      }`}
    >
      <div className="flex items-start gap-3">
        {/* 未読ドット (既読は幅だけ確保して行頭を揃える) */}
        <span
          className={`mt-2 h-2 w-2 shrink-0 rounded-full ${isRead ? 'bg-transparent' : 'bg-brand-500'}`}
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          {/* 1 行目: 依頼者名 + 主文 + 時刻 */}
          <div className="flex items-baseline gap-3">
            <p
              className={`min-w-0 flex-1 truncate text-sm ${isRead ? 'text-gray-600 dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'}`}
              data-testid="notification-primary"
            >
              {clientName && <span className="font-semibold">{clientName}</span>}
              <span className={`${clientName ? 'ml-3' : ''} font-medium`}>{primary}</span>
            </p>
            {timeLabel && (
              <span className="shrink-0 text-xs tabular-nums text-gray-400 dark:text-gray-500">
                {timeLabel}
              </span>
            )}
          </div>
          {/* 2 行目: 種類 · 事件種別 / 面 */}
          <p
            className="mt-1 flex items-center gap-1.5 truncate text-xs text-gray-500 dark:text-gray-400"
            data-testid="notification-breadcrumb"
          >
            <CategoryIcon category={category} />
            <span className="truncate">{breadcrumb}</span>
          </p>
          {/* 補足 (差し戻し理由・送信先・別内容の本文プレビューなど)。文字列は切り詰めない (省略は CSS) */}
          {supplements.map((line, i) => (
            <p
              key={i}
              className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400"
              data-testid="notification-supplement"
            >
              {line}
            </p>
          ))}
        </div>
      </div>
    </li>
  );
}
